import { useState, useEffect } from 'react'
import './Pagos.css'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4003/api/v1'

function Pagos() {
  const [pagos, setPagos] = useState([])
  const [loading, setLoading] = useState(true)
  const [filtroEstado, setFiltroEstado] = useState('')
  const [busqueda, setBusqueda] = useState('')
  const [metricas, setMetricas] = useState(null)
  const [paginacion, setPaginacion] = useState({ offset: 0, limit: 20, total: 0 })

  useEffect(() => {
    cargarPagos()
    cargarMetricas()
  }, [filtroEstado, paginacion.offset])

  const cargarPagos = async () => {
    setLoading(true)
    try {
      // Usar GraphQL para obtener pagos (necesitaríamos agregar el query)
      // Por ahora simulo con REST usando el historial
      const token = localStorage.getItem('token')
      const response = await fetch(
        `${API_URL.replace('/api/v1', '')}/graphql`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            query: `
              query ObtenerPagos($offset: Int, $limit: Int, $estado: String) {
                pagos(offset: $offset, limit: $limit, estado: $estado) {
                  id
                  referencia
                  comercio_nit
                  comercio_nombre
                  estado
                  monto_total
                  wompi_payment_method
                  tarjeta_ultimos_4
                  tarjeta_tipo
                  fecha_creacion
                  fecha_pago
                  licencia_renovada
                  plan {
                    nombre
                  }
                }
                pagosCount(estado: $estado)
              }
            `,
            variables: {
              offset: paginacion.offset,
              limit: paginacion.limit,
              estado: filtroEstado || null
            }
          })
        }
      )

      const data = await response.json()
      if (data.data) {
        setPagos(data.data.pagos || [])
        setPaginacion(prev => ({ ...prev, total: data.data.pagosCount || 0 }))
      }
    } catch (err) {
      console.error('Error cargando pagos:', err)
      // Si GraphQL falla, intentar con datos mock
      setPagos([])
    } finally {
      setLoading(false)
    }
  }

  const cargarMetricas = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(
        `${API_URL.replace('/api/v1', '')}/graphql`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            query: `
              query MetricasPagos {
                metricasPagos {
                  pagos_hoy
                  ingresos_hoy
                  pagos_semana
                  ingresos_semana
                  pagos_mes
                  ingresos_mes
                  pagos_total
                  ingresos_total
                  pagos_pendientes
                  monto_pendiente
                  tasa_conversion_mes
                }
              }
            `
          })
        }
      )

      const data = await response.json()
      if (data.data?.metricasPagos) {
        setMetricas(data.data.metricasPagos)
      }
    } catch (err) {
      console.error('Error cargando métricas:', err)
    }
  }

  const formatearMonto = (centavos) => {
    if (!centavos) return '$0'
    return `$${(centavos / 100).toLocaleString('es-CO')}`
  }

  const formatearFecha = (fecha) => {
    if (!fecha) return '-'
    return new Date(fecha).toLocaleDateString('es-CO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getEstadoBadge = (estado) => {
    const badges = {
      'APROBADO': 'badge-success',
      'PENDIENTE': 'badge-warning',
      'PROCESANDO': 'badge-info',
      'RECHAZADO': 'badge-danger',
      'ANULADO': 'badge-secondary',
      'ERROR': 'badge-danger',
      'EXPIRADO': 'badge-secondary'
    }
    return badges[estado] || 'badge-secondary'
  }

  const pagosFiltrados = pagos.filter(pago => {
    if (!busqueda) return true
    const term = busqueda.toLowerCase()
    return (
      pago.referencia?.toLowerCase().includes(term) ||
      pago.comercio_nit?.toLowerCase().includes(term) ||
      pago.comercio_nombre?.toLowerCase().includes(term)
    )
  })

  return (
    <div className="pagos-page">
      <header className="page-header">
        <div>
          <h1>Gestión de Pagos</h1>
          <p>Monitorea y gestiona los pagos de licencias</p>
        </div>
        <a
          href="/pagar"
          target="_blank"
          rel="noopener noreferrer"
          className="btn-portal"
        >
          Ver Portal Público
        </a>
      </header>

      {/* Métricas */}
      {metricas && (
        <div className="metricas-grid">
          <div className="metrica-card">
            <span className="metrica-label">Hoy</span>
            <span className="metrica-valor">{formatearMonto(metricas.ingresos_hoy)}</span>
            <span className="metrica-count">{metricas.pagos_hoy} pagos</span>
          </div>
          <div className="metrica-card">
            <span className="metrica-label">Esta Semana</span>
            <span className="metrica-valor">{formatearMonto(metricas.ingresos_semana)}</span>
            <span className="metrica-count">{metricas.pagos_semana} pagos</span>
          </div>
          <div className="metrica-card">
            <span className="metrica-label">Este Mes</span>
            <span className="metrica-valor">{formatearMonto(metricas.ingresos_mes)}</span>
            <span className="metrica-count">{metricas.pagos_mes} pagos</span>
          </div>
          <div className="metrica-card">
            <span className="metrica-label">Pendientes</span>
            <span className="metrica-valor warning">{formatearMonto(metricas.monto_pendiente)}</span>
            <span className="metrica-count">{metricas.pagos_pendientes} pagos</span>
          </div>
          <div className="metrica-card">
            <span className="metrica-label">Conversión (Mes)</span>
            <span className="metrica-valor">{metricas.tasa_conversion_mes || 0}%</span>
            <span className="metrica-count">aprobados/total</span>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="filtros-container">
        <div className="filtro-grupo">
          <input
            type="text"
            placeholder="Buscar por referencia, NIT o nombre..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="input-busqueda"
          />
        </div>
        <div className="filtro-grupo">
          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            className="select-filtro"
          >
            <option value="">Todos los estados</option>
            <option value="APROBADO">Aprobados</option>
            <option value="PENDIENTE">Pendientes</option>
            <option value="RECHAZADO">Rechazados</option>
            <option value="ANULADO">Anulados</option>
            <option value="EXPIRADO">Expirados</option>
          </select>
        </div>
        <button className="btn-refresh" onClick={() => { cargarPagos(); cargarMetricas(); }}>
          Actualizar
        </button>
      </div>

      {/* Tabla de pagos */}
      <div className="tabla-container">
        {loading ? (
          <div className="loading">
            <div className="spinner"></div>
            <p>Cargando pagos...</p>
          </div>
        ) : pagosFiltrados.length === 0 ? (
          <div className="empty-state">
            <p>No se encontraron pagos</p>
          </div>
        ) : (
          <table className="tabla-pagos">
            <thead>
              <tr>
                <th>Referencia</th>
                <th>Comercio</th>
                <th>Plan</th>
                <th>Monto</th>
                <th>Estado</th>
                <th>Método</th>
                <th>Fecha</th>
                <th>Licencia</th>
              </tr>
            </thead>
            <tbody>
              {pagosFiltrados.map(pago => (
                <tr key={pago.id}>
                  <td>
                    <span className="referencia">{pago.referencia}</span>
                  </td>
                  <td>
                    <div className="comercio-info">
                      <strong>{pago.comercio_nombre}</strong>
                      <span className="nit">{pago.comercio_nit}</span>
                    </div>
                  </td>
                  <td>{pago.plan?.nombre || '-'}</td>
                  <td className="monto">{formatearMonto(pago.monto_total)}</td>
                  <td>
                    <span className={`badge ${getEstadoBadge(pago.estado)}`}>
                      {pago.estado}
                    </span>
                  </td>
                  <td>
                    {pago.wompi_payment_method || '-'}
                    {pago.tarjeta_ultimos_4 && (
                      <span className="tarjeta-info">
                        •••• {pago.tarjeta_ultimos_4}
                      </span>
                    )}
                  </td>
                  <td>
                    <div className="fecha-info">
                      <span>{formatearFecha(pago.fecha_pago || pago.fecha_creacion)}</span>
                    </div>
                  </td>
                  <td>
                    {pago.licencia_renovada ? (
                      <span className="badge badge-success">Renovada</span>
                    ) : pago.estado === 'APROBADO' ? (
                      <span className="badge badge-warning">Pendiente</span>
                    ) : (
                      <span className="badge badge-secondary">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Paginación */}
      {paginacion.total > paginacion.limit && (
        <div className="paginacion">
          <button
            disabled={paginacion.offset === 0}
            onClick={() => setPaginacion(prev => ({ ...prev, offset: prev.offset - prev.limit }))}
          >
            Anterior
          </button>
          <span>
            Página {Math.floor(paginacion.offset / paginacion.limit) + 1} de{' '}
            {Math.ceil(paginacion.total / paginacion.limit)}
          </span>
          <button
            disabled={paginacion.offset + paginacion.limit >= paginacion.total}
            onClick={() => setPaginacion(prev => ({ ...prev, offset: prev.offset + prev.limit }))}
          >
            Siguiente
          </button>
        </div>
      )}
    </div>
  )
}

export default Pagos
